/**
 * TTRPGPanelView — dumb half of the live TTRPG side panel.
 *
 * Renders one of five visual states (`hidden`, `banner`, `dm`, `player`,
 * `observer`) selected by a discriminated `mode` prop. Owns no data —
 * everything (current visual, DM/player coaching, participation %, join
 * controls, "Ask Coach" pending flag) arrives as props from the smart
 * wrapper at ../index.tsx. The view never calls useCompanyApi / useQuery /
 * useMutation / useAuth / useTTRPGEvents itself; the only state it holds is
 * trivial UI state (which thumbnail is selected, whether the coach-brief
 * stack should expand).
 *
 * Theming note: category and coaching-type colours are Mantine semantic
 * palettes (brand, success, danger, warning, info, highlight) — never a
 * literal hue. The CSS module's per-tone left-borders mirror this so brand
 * swaps under [data-product] propagate without component edits.
 */
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Badge, Button, Divider, Loader, Paper, Popover, Stack, Text, UnstyledButton } from "@mantine/core"
import { useEffect, useState } from "react"
import type { IDMCoachingRequest, IDMParticipation, IDMSuggestion, IPlayerCoaching } from "../../../hooks/useSSE"
import type { IProgressiveVisual } from "../interfaces"
import styles from "../styles.module.css"
import type { IBannerViewProps, IDMViewProps, IObserverViewProps, IPlayerViewProps, TViewProps } from "./interfaces"

type TCategoryTone = "info" | "warning" | "danger" | "highlight" | "success" | "brand"

const CATEGORY_TONES: Record<string, TCategoryTone> = {
  pacing: "info",
  spotlight: "warning",
  tension: "danger",
  description: "highlight",
  engagement: "success",
  story_hook: "brand",
}

const COACHING_TYPE_TONES: Record<string, TCategoryTone> = {
  action: "info",
  roleplay: "highlight",
  interaction: "success",
}

function categoryTone(category: string): TCategoryTone {
  return CATEGORY_TONES[category] ?? "brand"
}

function coachingTypeTone(type: string): TCategoryTone {
  return COACHING_TYPE_TONES[type] ?? "brand"
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface IScenePanelProps {
  current: IProgressiveVisual
  history: IProgressiveVisual[]
  collapsed: boolean
  onToggle: () => void
}

function ScenePanel({ current, history, collapsed, onToggle }: IScenePanelProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [selected, setSelected] = useState<IProgressiveVisual>(current)

  // When a new image arrives, switch to it automatically
  useEffect(() => {
    setSelected(current)
  }, [current])

  const allImages = [current, ...history.filter((h) => h.artifactId !== current.artifactId)]

  return (
    <Paper withBorder className={styles.panel} p={0}>
      <UnstyledButton
        className={styles.header}
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={i18n._(msg`Toggle scene image panel`)}
      >
        <div className={styles.headerTitle}>
          <Text size="sm" fw={600}>
            <Trans>Scene</Trans>
          </Text>
        </div>
        <ActionIcon variant="subtle" size="sm" aria-hidden tabIndex={-1}>
          {collapsed ? "▲" : "▼"}
        </ActionIcon>
      </UnstyledButton>

      {!collapsed && (
        <>
          <img src={selected.url} alt={selected.prompt} className={styles.sceneImage} loading="lazy" />
          <Text className={styles.scenePrompt}>{selected.prompt}</Text>
          {allImages.length > 1 && (
            <div className={styles.thumbnailStrip}>
              {allImages.map((img) => (
                <UnstyledButton
                  key={img.artifactId ?? img.timestamp}
                  className={`${styles.thumbnailBtn} ${img.artifactId === selected.artifactId ? styles.thumbnailActive : ""}`}
                  onClick={() => setSelected(img)}
                  aria-label={img.prompt}
                  aria-pressed={img.artifactId === selected.artifactId}
                >
                  <img src={img.url} alt={img.prompt} className={styles.thumbnail} loading="lazy" />
                </UnstyledButton>
              ))}
            </div>
          )}
        </>
      )}
    </Paper>
  )
}

interface IParticipationBarsProps {
  data: IDMParticipation
}

function ParticipationBars({ data }: IParticipationBarsProps): React.JSX.Element | null {
  const entries = Object.entries(data.speakers).sort((a, b) => b[1] - a[1])
  if (entries.length === 0) return null

  return (
    <div className={styles.participationSection}>
      {entries.map(([name, pct]) => {
        const isLow = pct < 15
        return (
          <div key={name} className={styles.participationRow}>
            <span className={styles.participationName} title={name}>
              {name}
            </span>
            <div className={styles.participationBarWrap}>
              <div
                className={`${styles.participationBar} ${isLow ? styles.participationBarLow : ""}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${name}: ${pct}%`}
              />
            </div>
            <span className={`${styles.participationPct} ${isLow ? styles.participationPctLow : ""}`}>{pct}%</span>
          </div>
        )
      })}
    </div>
  )
}

interface IDMPanelProps {
  dmSuggestion: IDMSuggestion | null
  history: IDMSuggestion[]
  collapsed: boolean
  onToggle: () => void
  participationData: IDMParticipation | null
  coachingResult: IDMCoachingRequest | null
  coachingPending: boolean
  onAskCoach: () => void
}

function DMPanel({
  dmSuggestion,
  history,
  collapsed,
  onToggle,
  participationData,
  coachingResult,
  coachingPending,
  onAskCoach,
}: IDMPanelProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [hooksExpanded, setHooksExpanded] = useState(false)

  // If SSE / async delivery surfaces coaching, expand automatically
  useEffect(() => {
    if (coachingResult) setHooksExpanded(true)
  }, [coachingResult])

  const activeHooks = coachingResult?.hooks ?? []

  return (
    <Paper withBorder className={styles.panel} p={0}>
      <UnstyledButton
        className={styles.header}
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={i18n._(msg`Toggle DM coaching panel`)}
      >
        <div className={styles.headerTitle}>
          <Text size="sm" fw={600}>
            <Trans>DM coaching</Trans>
          </Text>
          {collapsed && dmSuggestion && (
            <Badge size="xs" color={categoryTone(dmSuggestion.category)} className={styles.pillBadge}>
              {dmSuggestion.category}
            </Badge>
          )}
        </div>
        <ActionIcon variant="subtle" size="sm" aria-hidden tabIndex={-1}>
          {collapsed ? "▲" : "▼"}
        </ActionIcon>
      </UnstyledButton>

      {!collapsed && (
        <>
          {participationData && <ParticipationBars data={participationData} />}

          <div className={styles.content}>
            {dmSuggestion ? (
              <div className={styles.suggestionCard}>
                <Badge size="xs" color={categoryTone(dmSuggestion.category)} mb="xs">
                  {dmSuggestion.category === "story_hook" ? "story hook" : dmSuggestion.category}
                  {dmSuggestion.targetPlayer ? ` — ${dmSuggestion.targetPlayer}` : ""}
                </Badge>
                <Text size="sm">{dmSuggestion.suggestion}</Text>
                {dmSuggestion.category === "story_hook" && dmSuggestion.storyWhy && (
                  <Text className={styles.storyWhy}>{dmSuggestion.storyWhy}</Text>
                )}
                {dmSuggestion.category === "spotlight" && dmSuggestion.participationPct !== undefined && (
                  <Text size="xs" c="dimmed" mt={4}>
                    <Trans>{dmSuggestion.participationPct}% of words so far</Trans>
                  </Text>
                )}
              </div>
            ) : (
              <Text size="xs" c="dimmed" mb="sm">
                <Trans>Waiting for session activity...</Trans>
              </Text>
            )}

            {history.length > 0 && (
              <>
                <Divider mb="xs" label={i18n._(msg`Recent`)} labelPosition="left" />
                <Stack gap={4}>
                  {history.map((item) => (
                    <div key={item.timestamp} className={styles.historyItem} data-tone={categoryTone(item.category)}>
                      <Badge size="xs" color={categoryTone(item.category)} mb={2}>
                        {item.category === "story_hook" ? "story hook" : item.category}
                        {item.targetPlayer ? ` — ${item.targetPlayer}` : ""}
                      </Badge>
                      <Text size="xs" c="dimmed">
                        {item.suggestion}
                      </Text>
                      {item.category === "story_hook" && item.storyWhy && (
                        <Text size="xs" c="dimmed" fs="italic">
                          {item.storyWhy}
                        </Text>
                      )}
                    </div>
                  ))}
                </Stack>
              </>
            )}

            {activeHooks.length > 0 && hooksExpanded && (
              <div className={styles.hooksSection}>
                <Text size="xs" fw={600} mb="xs" c="brand">
                  <Trans>Coach brief</Trans>
                </Text>
                <Stack gap={4}>
                  {activeHooks.map((hook) => (
                    <div key={hook.suggestion} className={styles.hookCard}>
                      <Text size="xs">{hook.suggestion}</Text>
                      <Text className={styles.hookWhy}>{hook.why}</Text>
                      {hook.character && (
                        <Text size="xs" c="dimmed">
                          <Trans>Focus: {hook.character}</Trans>
                        </Text>
                      )}
                    </div>
                  ))}
                </Stack>
              </div>
            )}
          </div>

          <div className={styles.coachFooter}>
            <Button size="xs" variant="light" color="brand" loading={coachingPending} onClick={onAskCoach} fullWidth>
              <Trans>Ask coach</Trans>
            </Button>
          </div>
        </>
      )}
    </Paper>
  )
}

interface IPlayerPanelProps {
  playerCoaching: IPlayerCoaching | null
  history: IPlayerCoaching[]
  characterName: string | null
  collapsed: boolean
  onToggle: () => void
}

function PlayerPanel({
  playerCoaching,
  history,
  characterName,
  collapsed,
  onToggle,
}: IPlayerPanelProps): React.JSX.Element {
  const { i18n } = useLingui()
  return (
    <Paper withBorder className={styles.panel} p={0}>
      <UnstyledButton
        className={styles.header}
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={i18n._(msg`Toggle player coaching panel`)}
      >
        <div className={styles.headerTitle}>
          <Text size="sm" fw={600}>
            {characterName ? i18n._(msg`Your Coach — ${characterName}`) : i18n._(msg`Your Coach`)}
          </Text>
          {collapsed && playerCoaching && (
            <Badge size="xs" color={coachingTypeTone(playerCoaching.type)} className={styles.pillBadge}>
              {playerCoaching.type}
            </Badge>
          )}
        </div>
        <ActionIcon variant="subtle" size="sm" aria-hidden tabIndex={-1}>
          {collapsed ? "▲" : "▼"}
        </ActionIcon>
      </UnstyledButton>

      {!collapsed && (
        <div className={styles.content}>
          {playerCoaching ? (
            <div className={styles.suggestionCard}>
              <Badge size="xs" color={coachingTypeTone(playerCoaching.type)} mb="xs">
                {playerCoaching.type}
              </Badge>
              <Text size="sm">{playerCoaching.suggestion}</Text>
            </div>
          ) : (
            <Text size="xs" c="dimmed" mb="sm">
              <Trans>Waiting for session activity...</Trans>
            </Text>
          )}

          {history.length > 0 && (
            <>
              <Divider mb="xs" label={i18n._(msg`Recent`)} labelPosition="left" />
              <Stack gap={4}>
                {history.map((item) => (
                  <div key={item.timestamp} className={styles.historyItem} data-tone={coachingTypeTone(item.type)}>
                    <Badge size="xs" color={coachingTypeTone(item.type)} mb={2}>
                      {item.type}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {item.suggestion}
                    </Text>
                  </div>
                ))}
              </Stack>
            </>
          )}
        </div>
      )}
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Banner — join controls before a role is picked
// ---------------------------------------------------------------------------

function JoinBanner({
  charPickerOpen,
  onCharPickerChange,
  characters,
  charactersLoading,
  joinDMPending,
  joinPlayerPending,
  onJoinDM,
  onJoinPlayer,
  onJoinObserver,
}: Omit<IBannerViewProps, "mode">): React.JSX.Element {
  return (
    <Paper withBorder className={styles.banner} p="sm">
      <div className={styles.bannerContent}>
        <Text size="sm">
          <Trans>Join this session to receive real-time coaching</Trans>
        </Text>
        <Button size="xs" variant="filled" loading={joinDMPending} onClick={onJoinDM}>
          <Trans>Join as DM</Trans>
        </Button>

        <Popover opened={charPickerOpen} onChange={onCharPickerChange} position="top" withArrow>
          <Popover.Target>
            <Button
              size="xs"
              variant="light"
              loading={joinPlayerPending}
              onClick={() => onCharPickerChange(!charPickerOpen)}
            >
              <Trans>Join as player</Trans>
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            {charactersLoading ? (
              <Loader size="xs" />
            ) : characters.length === 0 ? (
              <Text size="xs" c="dimmed">
                <Trans>No characters in this series yet.</Trans>
              </Text>
            ) : (
              <Stack gap={4}>
                {characters.map((char) => (
                  <Button
                    key={char.id}
                    variant="subtle"
                    size="xs"
                    onClick={() => {
                      onCharPickerChange(false)
                      onJoinPlayer(char.id)
                    }}
                  >
                    {char.name}
                    {char.characterClass ? ` (${char.characterClass})` : ""}
                  </Button>
                ))}
              </Stack>
            )}
          </Popover.Dropdown>
        </Popover>

        <Button variant="subtle" size="xs" className={styles.observerLink} onClick={onJoinObserver}>
          <Trans>Join as observer</Trans>
        </Button>
      </div>
    </Paper>
  )
}

// ---------------------------------------------------------------------------
// Joined-role containers
// ---------------------------------------------------------------------------

function maybeScenePanel(
  currentVisual: IProgressiveVisual | null,
  visualHistory: IProgressiveVisual[],
  sceneCollapsed: boolean,
  onSceneToggle: () => void,
): React.JSX.Element | null {
  if (!currentVisual) return null
  return (
    <ScenePanel current={currentVisual} history={visualHistory} collapsed={sceneCollapsed} onToggle={onSceneToggle} />
  )
}

function DMView(props: Omit<IDMViewProps, "mode">): React.JSX.Element {
  const scenePanel = maybeScenePanel(
    props.currentVisual,
    props.visualHistory,
    props.sceneCollapsed,
    props.onSceneToggle,
  )
  return (
    <div className={styles.container}>
      {scenePanel}
      <DMPanel
        dmSuggestion={props.dmSuggestion}
        history={props.dmHistory}
        collapsed={props.collapsed}
        onToggle={props.onToggle}
        participationData={props.participationData}
        coachingResult={props.coachingResult}
        coachingPending={props.coachingPending}
        onAskCoach={props.onAskCoach}
      />
    </div>
  )
}

function PlayerView(props: Omit<IPlayerViewProps, "mode">): React.JSX.Element {
  const scenePanel = maybeScenePanel(
    props.currentVisual,
    props.visualHistory,
    props.sceneCollapsed,
    props.onSceneToggle,
  )
  return (
    <div className={styles.container}>
      {scenePanel}
      <PlayerPanel
        playerCoaching={props.playerCoaching}
        history={props.playerHistory}
        characterName={props.characterName}
        collapsed={props.collapsed}
        onToggle={props.onToggle}
      />
    </div>
  )
}

function ObserverView(props: Omit<IObserverViewProps, "mode">): React.JSX.Element | null {
  const scenePanel = maybeScenePanel(
    props.currentVisual,
    props.visualHistory,
    props.sceneCollapsed,
    props.onSceneToggle,
  )
  if (!scenePanel) return null
  return <div className={styles.container}>{scenePanel}</div>
}

// ---------------------------------------------------------------------------
// Main TTRPGPanelView — switches on `mode`
// ---------------------------------------------------------------------------

export function TTRPGPanelView(props: TViewProps): React.JSX.Element | null {
  if (props.mode === "hidden") return null
  if (props.mode === "banner") return <JoinBanner {...props} />
  if (props.mode === "dm") return <DMView {...props} />
  if (props.mode === "player") return <PlayerView {...props} />
  return <ObserverView {...props} />
}
